import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

import { PreviewSheet } from '../../PreviewSheet';
import { ExternalLinkIcon } from '@icons/ExternalLinkIcon';
import { useOrderSource } from '@hooks/orderSources';
import { useTranslation } from '@hooks/useTranslation';

import { OrderSourceOverviewSection } from './OrderSourceOverviewSection';
import { OrderSourceStatusDropdown } from './OrderSourceStatusDropdown';

import type { OrderSource } from '../types';
import { Button } from '@/components/ui/button';

interface OrderSourcePreviewDrawerProps {
  orderSource: OrderSource | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // RBAC: gates the header status dropdown + footer Edit link.
  canWrite?: boolean;
}

function DrawerHeader({
  orderSource,
  onClose,
  canWrite,
}: {
  orderSource: OrderSource;
  onClose: () => void;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const label = t('preview_drawer.header.view_source_details', 'View source details');

  return (
    <div className="flex w-full items-center justify-between">
      <div className="flex items-center gap-3">
        <Link
          to="/order_sources/$id"
          params={{ id: String(orderSource.id) }}
          onClick={onClose}
          className="text-grey transition-colors hover:text-off-black"
          aria-label={label}
        >
          <ExternalLinkIcon />
        </Link>
        <SheetTitle asChild>
          <span className="font-inter text-lg font-semibold text-off-black">{label}</span>
        </SheetTitle>
      </div>
      {canWrite && (
        <OrderSourceStatusDropdown
          orderSourceId={orderSource.id}
          active={orderSource.active}
        />
      )}
    </div>
  );
}

export function OrderSourcePreviewDrawer({
  orderSource,
  open,
  onOpenChange,
  canWrite = false,
}: OrderSourcePreviewDrawerProps) {
  // Keep the last non-null record so the drawer stays populated during Radix's
  // close animation (parent resets to null on close).
  const [stableItem, setStableItem] = useState<OrderSource | null>(orderSource);
  useEffect(() => {
    if (orderSource !== null) setStableItem(orderSource);
  }, [orderSource]);
  const activeItem = orderSource ?? stableItem;

  if (!activeItem) return null;

  return (
    <OrderSourcePreviewDrawerContent
      orderSource={activeItem}
      open={open}
      onOpenChange={onOpenChange}
      canWrite={canWrite}
    />
  );
}

function OrderSourcePreviewDrawerContent({
  orderSource,
  open,
  onOpenChange,
  canWrite,
}: {
  orderSource: OrderSource;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canWrite: boolean;
}) {
  const { t } = useTranslation();
  const { data } = useOrderSource(orderSource.id, {
    initialData: { order_source: orderSource },
  });
  const current = data ?? orderSource;
  const isInactive = !current.active;

  return (
    <PreviewSheet
      open={open}
      onOpenChange={onOpenChange}
      header={
        <DrawerHeader
          orderSource={current}
          onClose={() => onOpenChange(false)}
          canWrite={canWrite}
        />
      }
      footerClassName="justify-end"
      footer={
        canWrite ? (
          <Link to="/order_sources/$id/edit" params={{ id: String(current.id) }}>
            <Button variant="outline-subtle">{t('preview_drawer.footer.edit', 'Edit')}</Button>
          </Link>
        ) : undefined
      }
    >
      <div
        data-testid="order-source-preview-body"
        className={cn(isInactive && 'opacity-50')}
      >
        <OrderSourceOverviewSection orderSource={current} />
      </div>
    </PreviewSheet>
  );
}
